import {distinctDomainToFieldName, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import {assert} from 'Asserts';
import {
  RegistrationTableRowData,
  SchemaInterfaceColumnTransformerBase,
} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {RegistrationTableMetadataQuery_implementedAntibodySchemaInterface_ImplementedAntibodySchemaInterfaceNode as ImplementedAntibodyInterface} from 'notebook/structured-tables/components/modals/__generated/RegistrationTableMetadataQuery';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {
  AntibodyDomainStructuredTableColumn,
  ComplexPolymerComponentFrontEndMetadata,
  StructuredTableColumn,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/TableConstants';

function isAntibodyInterface(
  implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
): implementedSchemaInterface is ImplementedAntibodyInterface {
  return implementedSchemaInterface.__typename === 'ImplementedAntibodySchemaInterfaceNode';
}

export default class AntibodyRegTableColumnTransformer implements SchemaInterfaceColumnTransformerBase {
  replaceExistingColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    registeredTableRowMetadata: RegistrationTableRowData
  ): StructuredTableColumn | null {
    return null;
  }

  getNewColumns(
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): Array<AntibodyDomainStructuredTableColumn> {
    assert(
      isAntibodyInterface(implementedSchemaInterface),
      'Antibody transformer called with non-antibody interface'
    );
    const sortedDistinctDomains = formatToSortedDistinctDomains(implementedSchemaInterface.format);
    return sortedDistinctDomains.map((domain) => ({
      ...makeStructuredTableColumn({
        columnType: TableColumnType.ANTIBODY_DOMAIN,
        name: distinctDomainToFieldName(domain),
        isRequired: true,
        isReadOnly: false,
      }),
      columnType: TableColumnType.ANTIBODY_DOMAIN,
      columnMetadata: {
        domainType: domain.type,
        specificity: domain.specificity,
      },
    }));
  }

  shouldHideColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): boolean {
    assert(
      isAntibodyInterface(implementedSchemaInterface),
      'Antibody transformer called with non-antibody interface'
    );
    const allDomainNames = implementedSchemaInterface.format.domains.map((domain) =>
      distinctDomainToFieldName(domain)
    );
    if (column.columnType === TableColumnType.TAG_VALUE) {
      const tagValueColumn = column as TagValueStructuredTableColumn;
      // Hide all computed field columns which match domain names. Ideally, we'd rely on the computed field
      // definition to determine if it's a domain column, but that's not available here. The SSI guarantees
      // that all domain names are reserved for the domain computed fields.
      // See benchling/antibodies/schema/domain/antibody_schema_interface_validator.py
      if (!tagValueColumn.schemaFieldIsComputed) {
        return false;
      }
      return allDomainNames.includes(tagValueColumn.schemaFieldDisplayName ?? '');
    }
    return false;
  }

  getFrontEndMetadata(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): ComplexPolymerComponentFrontEndMetadata | null {
    // TODO(BNCH-128762) Use this to do the schema field stuff.
    return null;
  }
}
